import { useEffect, useRef, useState } from 'react';
import { Search, X, Check } from 'lucide-react';
import { visitReportsApi } from '../../services/visitReportsApi';

interface ProductHit {
    productId: string;
    productName: string;
    company: string;
}

interface ProductPickerProps {
    companyCode: string | undefined;
    productCode: string | undefined;
    productName: string;
    onPick: (match: { productCode: string; productName: string } | null) => void;
    placeholder?: string;
}

/**
 * Searchable dropdown that lets the sales rep assign an ERP product to the
 * entry in productsDiscussed. Debounced server-side search against
 * etl.Product scoped to the customer's CompanyCode. Replaces a plain text
 * input so the stored productCode always points to a real item in the master.
 */
export function ProductPicker({ companyCode, productCode, productName, onPick, placeholder }: ProductPickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [hits, setHits] = useState<ProductHit[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [isOpen]);

    // Auto-focus search when opened
    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    // Debounced fetch
    useEffect(() => {
        if (!isOpen || !companyCode) return;
        setError(null);
        setLoading(true);
        const handle = setTimeout(() => {
            const term = search.trim() || productName || '';
            visitReportsApi.searchProducts(companyCode, term, 50)
                .then(setHits)
                .catch(err => setError(err?.message || 'Search failed'))
                .finally(() => setLoading(false));
        }, 250);
        return () => clearTimeout(handle);
    }, [isOpen, search, companyCode, productName]);

    const clear = () => {
        onPick(null);
        setIsOpen(false);
        setSearch('');
    };

    if (!companyCode) {
        return (
            <div className="px-3 py-2 border border-gray-200 rounded-lg text-xs text-gray-400 italic">
                Select a customer first to pick a product
            </div>
        );
    }

    return (
        <div ref={containerRef} className="relative">
            <button
                type="button"
                onClick={() => setIsOpen(v => !v)}
                className={`w-full flex items-center justify-between gap-1 px-3 py-2 border rounded-lg text-sm text-left bg-white hover:bg-gray-50 ${productCode ? 'border-green-300' : 'border-gray-300'}`}
            >
                <span className={`truncate min-w-0 flex-1 ${productCode ? 'text-gray-900' : 'text-gray-400'}`}>
                    {productCode ? (
                        <>
                            <span className="text-gray-900">{productName || '(no name)'}</span>
                            <span className="font-mono text-[11px] text-gray-500 ml-1">({productCode})</span>
                        </>
                    ) : (
                        placeholder ?? 'Search product code …'
                    )}
                </span>
                <span className="flex items-center gap-1 flex-shrink-0">
                    {productCode && (
                        <span
                            onClick={e => { e.stopPropagation(); clear(); }}
                            className="text-gray-400 hover:text-red-600"
                            title="Clear"
                        >
                            <X className="w-3.5 h-3.5" />
                        </span>
                    )}
                    <Search className="w-3.5 h-3.5 text-gray-400" />
                </span>
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-lg">
                    <div className="p-2 border-b border-gray-100">
                        <input
                            ref={inputRef}
                            type="text"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder={productName ? `Search (e.g. "${productName.slice(0, 30)}")` : 'Search product …'}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                        {loading && <div className="px-3 py-2 text-xs text-gray-400">Searching …</div>}
                        {error && <div className="px-3 py-2 text-xs text-red-600">{error}</div>}
                        {!loading && !error && hits.length === 0 && (
                            <div className="px-3 py-3 text-xs text-gray-400 text-center">No matches in this company</div>
                        )}
                        {hits.map(h => {
                            const selected = h.productId === productCode;
                            return (
                                <button
                                    key={h.productId}
                                    type="button"
                                    onClick={() => {
                                        onPick({ productCode: h.productId, productName: h.productName });
                                        setIsOpen(false);
                                        setSearch('');
                                    }}
                                    className={`w-full text-left px-3 py-2 hover:bg-blue-50 flex items-start gap-2 ${selected ? 'bg-blue-50' : ''}`}
                                >
                                    {selected && <Check className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />}
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-gray-800 truncate">{h.productName}</div>
                                        <div className="text-[11px] font-mono text-gray-500">{h.productId}</div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
